import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Button, Icon, Input, Radio, Select, Switch, Table, Tabs, message } from 'antd';
import {
  ChartDataTab,
  ChartFontsizeSettings,
  ChartGeneralSettings,
  ChartStylesSettings,
  ChartTypeContainer,
  ChartTypeSettings,
  CollapseContainer,
} from '../styles';
import {
  ChartTypesIcon,
  FontSettingsIcon,
  GeneralSettingsIcon,
  ArrowDown,
  chartStylesIcon,
} from '../chartIcons';
import { FontstyleCombobox } from './fontStyleComboBox';
import { fetchChart, getChartPayload, getUniqueRgb } from '../chartHelper';
import { EditableCell } from './editableCell';
import { ChartColorCell } from './chartColorCell';
import { CHART_TRANSLATION } from '../translationValues';
import {
  LABEL_FORMATS,
  LABEL_TYPES,
  getChartStyles,
  getPieChartTypesList,
} from '../chartConstants';
import regex from '../../../../../utils/regex';
import { debounce } from 'underscore';
import { deepCopy } from '../../../../../utils/javascript';
import {
  ChartCollapse,
  ChartSelect,
  CollapsePanel,
  panelStyle,
  useCheckTableInFocus,
} from './chartSidebarHelper';

const { TabPane } = Tabs;
const { Option } = Select;

export const PieChartSettings = ({ element, updateComplexElementDetails, t, globalVariables, isInteractiveDesign }) => {
  const { contents, uuid, style } = element;
  const {
    type,
    dataSource,
    showLegend,
    showLabels,
    legendPosition,
    formatValueType,
    labelFontSize,
    valueFontSize,
    descriptionFontSize,
    labelColor,
    valueColor,
    descriptionColor,
    chartStyle,
    baseVariable,
    labelVariable,
    valueVariable,
    descVariable,
  } = contents;

  const { isVariable } = style;
  const [chartName, setChartName] = useState(baseVariable);
  const chartTypesList = useMemo(() => getPieChartTypesList(t), []);
  const chartStyles = useMemo(() => getChartStyles(t), []);
  const allowedVariables = useMemo(() => {
    const filterVariableList = [labelVariable, valueVariable, descVariable];
    return globalVariables.filter(({ type, placeHolderText }) => {
      if (type === 'String' && !filterVariableList.includes(placeHolderText)) {
        return true;
      }
      return false;
    });
  }, [labelVariable, valueVariable, descVariable, globalVariables]);
  const [tableRef, tableKey] = useCheckTableInFocus();

  useEffect(() => {
    setChartName(baseVariable);
  }, [baseVariable]);

  const columns = useMemo(
    () => [
      {
        title: '',
        dataIndex: 'more',
        key: 'more',
        render: (text, record, index) => (
          <ChartColorCell index={index} deleteRow={deleteRow} addRow={addRow} t={t} />
        ),
      },
      {
        title: t(CHART_TRANSLATION.LABEL.key, CHART_TRANSLATION.LABEL.value),
        dataIndex: 'label',
        key: 'label',
        render: (text, record) => (
          <EditableCell record={record} text={text} id="label" updateSource={updateSource} />
        ),
      },
      {
        title: t(CHART_TRANSLATION.VALUE_LABEL.key, CHART_TRANSLATION.VALUE_LABEL.value),
        dataIndex: 'value',
        key: 'value',
        render: (text, record) => (
          <EditableCell record={record} text={text} id="value" updateSource={updateSource} />
        ),
      },
      {
        title: t(
          CHART_TRANSLATION.DESCRIPTION_LABEL.key,
          CHART_TRANSLATION.DESCRIPTION_LABEL.value,
        ),
        dataIndex: 'description',
        key: 'description',
        render: (text, record) => (
          <EditableCell record={record} text={text} id="description" updateSource={updateSource} />
        ),
      },
    ],
    [dataSource],
  );

  const updateChartDetails = async (updatedValue, key, skipChartCreation = false) => {
    let copiedContent;
    if (key) {
      copiedContent = deepCopy(element).contents;
      copiedContent[key] = updatedValue;
    } else {
      copiedContent = updatedValue;
    }
    const payload = getChartPayload(copiedContent, isVariable);
    try {
      let data;
      if (!skipChartCreation) {
        data = await fetchChart(payload);
        copiedContent.dataUrl = `data:image/png;base64, ${data.base64ImageBytes}`;
      }
      copiedContent.dataRequest = payload;
      updateComplexElementDetails(uuid, copiedContent, 'contents');
    } catch (error) {
      message.error(
        t(CHART_TRANSLATION.CHART_UPDATE_ERROR.key, CHART_TRANSLATION.CHART_UPDATE_ERROR.value),
      );
    }
  };

  const updateChartWithStyle = async (copiedContent, copiedStyle) => {
    const payload = getChartPayload(copiedContent, copiedStyle.isVariable);
    try {
      copiedContent.dataRequest = payload;
      updateComplexElementDetails(
        uuid,
        { contents: copiedContent, style: copiedStyle },
        'contents-with-style',
      );
    } catch (error) {
      message.error(
        t(CHART_TRANSLATION.CHART_UPDATE_ERROR.key, CHART_TRANSLATION.CHART_UPDATE_ERROR.value),
      );
    }
  };

  const debouncedUpdateChartDetails = useCallback(debounce(updateChartDetails, 300), []);

  const updateSource = (record, key, changedValue) => {
    const tempDataSource = deepCopy(dataSource).map(item => {
      // check for color equality as color is the unique for each item.
      if (item.color.toString() === record.color.toString()) {
        item[key] = changedValue;
      }
      return item;
    });
    updateChartDetails(tempDataSource, 'dataSource');
  };

  const deleteRow = index => {
    const tempDataSource = deepCopy(dataSource).filter((i, itemIndex) => itemIndex !== index);
    updateChartDetails(tempDataSource, 'dataSource');
  };

  const addRow = index => {
    const tempDataSource = deepCopy(dataSource);
    const newRow = {
      label: '',
      value: '',
      description: '',
      color: getUniqueRgb(dataSource.map(({ color }) => color)),
    };
    const newSource = [...tempDataSource.slice(0, index), newRow, ...tempDataSource.slice(index)];
    updateChartDetails(newSource, 'dataSource');
  };

  const toggleLegend = checked => {
    if (chartStyle === LABEL_TYPES.WITH_DESCRIPTION) return;
    updateChartDetails(checked, 'showLegend');
  };

  const toggleLabels = checked => {
    if (chartStyle === LABEL_TYPES.WITH_DESCRIPTION) {
      return;
    }
    updateChartDetails(checked, 'showLabels');
  };

  const setLegendPosition = value => {
    updateChartDetails(value, 'legendPosition');
  };

  const setFormatValueType = value => {
    updateChartDetails(value, 'formatValueType');
  };

  const setLabelFontsize = value => {
    updateChartDetails(value, 'labelFontSize');
  };

  const setValueFontsize = value => {
    updateChartDetails(value, 'valueFontSize');
  };

  const setDescriptionFontsize = value => {
    updateChartDetails(value, 'descriptionFontSize');
  };

  const setChartStyle = value => {
    const { contents: copiedContent } = deepCopy(element);
    copiedContent.chartStyle = value;
    copiedContent.showLabels = true;
    copiedContent.showLegend = false;
    updateChartDetails(copiedContent);
  };

  const setLabelFontColor = ({ rgb }) => {
    updateChartDetails([rgb.r, rgb.g, rgb.b], 'labelColor');
  };

  const setValueFontColor = ({ rgb }) => {
    updateChartDetails([rgb.r, rgb.g, rgb.b], 'valueColor');
  };

  const setDescFontColor = ({ rgb }) => {
    updateChartDetails([rgb.r, rgb.g, rgb.b], 'descriptionColor');
  };

  const setChartType = ({ type }) => {
    const { contents: copiedContent } = deepCopy(element);
    copiedContent.type = type;
    updateChartDetails(copiedContent);
  };

  const setDynamicChart = value => {
    const { style: copiedStyle, contents: copiedContent } = deepCopy(element);
    copiedStyle.isVariable = value;
    updateChartWithStyle(copiedContent, copiedStyle);
  };

  const setBaseVariable = e => {
    if (regex.NO_SPLCHAR_EXCEPT_HYPHEN.test(e.target.value)) {
      message.error(t(CHART_TRANSLATION.SPECIAL_CHAR_ERROR.key, CHART_TRANSLATION.SPECIAL_CHAR_ERROR.value));
      return;
    }
    setChartName(e.target.value);
    debouncedUpdateChartDetails(e.target.value, 'baseVariable', true);
  };

  const setLabelVariable = value => {
    updateChartDetails(value, 'labelVariable', true);
  };

  const setValueVariable = value => {
    updateChartDetails(value, 'valueVariable', true);
  };

  const setDescVariable = value => {
    updateChartDetails(value, 'descVariable', true);
  };

  return (
    <Tabs defaultActiveKey="1" style={{ overflow: 'unset' }}>
      <TabPane
        tab={t(CHART_TRANSLATION.DATA_LABEL.key, CHART_TRANSLATION.DATA_LABEL.value)}
        key="1"
      >
        <ChartDataTab>
          <label id="dynamic-chart-switch">
            <span>
              {t(
                CHART_TRANSLATION.DYNAMIC_CHART_QUESTION.key,
                CHART_TRANSLATION.DYNAMIC_CHART_QUESTION.value,
              )}
            </span>
            <Switch checked={!!isVariable} onChange={checked => setDynamicChart(checked)} disabled={isInteractiveDesign}></Switch>
          </label>
          {!isVariable ? (
            <>
              <div ref={tableRef}>
                <Table
                  columns={columns}
                  dataSource={dataSource}
                  pagination={false}
                  size="middle"
                  bordered
                  key={tableKey}
                  tableLayout="auto"
                />
              </div>
              <Button type="link" onClick={() => addRow(dataSource.length)}>
                <Icon type="plus" />{' '}
                {t(CHART_TRANSLATION.ADD_ROW_LABEL.key, CHART_TRANSLATION.ADD_ROW_LABEL.value)}
              </Button>
            </>
          ) : (
            <div>
              <label>
                <span>
                  {t(CHART_TRANSLATION.NAME_LABEL.key, CHART_TRANSLATION.NAME_LABEL.value)}
                </span>
                <Input value={chartName} onChange={setBaseVariable} />
              </label>
              <label>
                <span>{t(CHART_TRANSLATION.LABEL.key, CHART_TRANSLATION.LABEL.value)}</span>
                <ChartSelect t={t} value={labelVariable} onChange={setLabelVariable}>
                  {allowedVariables.map(item => (
                    <Option value={item.id}>{item.placeHolderText}</Option>
                  ))}
                </ChartSelect>
              </label>
              <label>
                <span>
                  {t(CHART_TRANSLATION.VALUE_LABEL.key, CHART_TRANSLATION.VALUE_LABEL.value)}
                </span>
                <ChartSelect t={t} value={valueVariable} onChange={setValueVariable}>
                  {allowedVariables.map(item => (
                    <Option value={item.id}>{item.placeHolderText}</Option>
                  ))}
                </ChartSelect>
              </label>
              <label>
                <span>
                  {t(CHART_TRANSLATION.DESC_LABEL.key, CHART_TRANSLATION.DESC_LABEL.value)}
                </span>
                <ChartSelect t={t} value={descVariable} onChange={setDescVariable}>
                  {allowedVariables.map(item => (
                    <Option value={item.id}>{item.placeHolderText}</Option>
                  ))}
                </ChartSelect>
              </label>
            </div>
          )}
        </ChartDataTab>
      </TabPane>
      <TabPane
        tab={t(CHART_TRANSLATION.SETTINGS_LABEL.key, CHART_TRANSLATION.SETTINGS_LABEL.value)}
        key="2"
      >
        <CollapseContainer>
          <ChartCollapse defaultActiveKey="1">
            <CollapsePanel
              header={
                <div>
                  <Icon component={GeneralSettingsIcon} />{' '}
                  <span>
                    {t(CHART_TRANSLATION.GENERAL_LABEL.key, CHART_TRANSLATION.GENERAL_LABEL.value)}
                  </span>
                  <span>
                    <Icon component={ArrowDown} />
                  </span>
                </div>
              }
              key="1"
              style={panelStyle}
            >
              <ChartGeneralSettings>
                <label id="pie-format-values-at">
                  <div>
                    {t(
                      CHART_TRANSLATION.FORMAT_VALUES_AT.key,
                      CHART_TRANSLATION.FORMAT_VALUES_AT.value,
                    )}
                  </div>
                  <Radio.Group
                    value={formatValueType}
                    onChange={e => setFormatValueType(e.target.value)}
                    defaultValue={LABEL_FORMATS.PERCENTAGE}
                  >
                    <Radio.Button value={LABEL_FORMATS.PERCENTAGE}>
                      {t(
                        CHART_TRANSLATION.PERCENTAGES_LABEL.key,
                        CHART_TRANSLATION.PERCENTAGES_LABEL.value,
                      )}
                    </Radio.Button>
                    <Radio.Button value={LABEL_FORMATS.NUMBER}>
                      {t(
                        CHART_TRANSLATION.NUMBERS_LABEL.key,
                        CHART_TRANSLATION.NUMBERS_LABEL.value,
                      )}
                    </Radio.Button>
                  </Radio.Group>
                </label>
                <label>
                  <span>
                    {t(
                      CHART_TRANSLATION.SHOW_LEGEND_LABEL.key,
                      CHART_TRANSLATION.SHOW_LEGEND_LABEL.value,
                    )}
                  </span>
                  <Switch checked={showLegend} onChange={checked => toggleLegend(checked)} />
                </label>
                <label>
                  <span>
                    {t(
                      CHART_TRANSLATION.SHOW_LABELS_LABEL.key,
                      CHART_TRANSLATION.SHOW_LABELS_LABEL.value,
                    )}
                  </span>
                  <Switch checked={showLabels} onChange={checked => toggleLabels(checked)} />
                </label>
                {showLegend && (
                  <label>
                    <span>
                      {t(
                        CHART_TRANSLATION.LEGEND_POSITION_LABEL.key,
                        CHART_TRANSLATION.LEGEND_POSITION_LABEL.value,
                      )}
                    </span>
                    <Select
                      value={legendPosition}
                      onSelect={value => setLegendPosition(value)}
                      suffixIcon={<Icon type="caret-down" />}
                    >
                      <Option value="top">
                        {t(CHART_TRANSLATION.TOP_LABEL.key, CHART_TRANSLATION.TOP_LABEL.value)}
                      </Option>
                      <Option value="bottom">
                        {t(
                          CHART_TRANSLATION.BOTTOM_LABEL.key,
                          CHART_TRANSLATION.BOTTOM_LABEL.value,
                        )}
                      </Option>
                      <Option value="left">
                        {t(CHART_TRANSLATION.LEFT_LABEL.key, CHART_TRANSLATION.LEFT_LABEL.value)}
                      </Option>
                      <Option value="right">
                        {t(CHART_TRANSLATION.RIGHT_LABEL.key, CHART_TRANSLATION.RIGHT_LABEL.value)}
                      </Option>
                    </Select>
                  </label>
                )}
              </ChartGeneralSettings>
            </CollapsePanel>
          </ChartCollapse>
          <ChartCollapse>
            <CollapsePanel
              header={
                <div>
                  <Icon component={FontSettingsIcon} />{' '}
                  <span>
                    {t(
                      CHART_TRANSLATION.FONTSTYLES_LABEL.key,
                      CHART_TRANSLATION.FONTSTYLES_LABEL.value,
                    )}
                  </span>
                  <span>
                    <Icon component={ArrowDown} />
                  </span>
                </div>
              }
              key="1"
              style={panelStyle}
            >
              <ChartFontsizeSettings>
                <label onClick={e => e.preventDefault()}>
                  <span>{t(CHART_TRANSLATION.LABEL.key, CHART_TRANSLATION.LABEL.value)}</span>
                  <FontstyleCombobox
                    t={t}
                    onSizeChange={setLabelFontsize}
                    fontColor={labelColor}
                    fontSize={labelFontSize}
                    onColorChange={setLabelFontColor}
                  />
                </label>
                <label onClick={e => e.preventDefault()}>
                  <span>
                    {t(CHART_TRANSLATION.VALUE_LABEL.key, CHART_TRANSLATION.VALUE_LABEL.value)}
                  </span>
                  <FontstyleCombobox
                    t={t}
                    onSizeChange={setValueFontsize}
                    fontColor={valueColor}
                    fontSize={valueFontSize}
                    onColorChange={setValueFontColor}
                  />
                </label>
                <label onClick={e => e.preventDefault()}>
                  <span>
                    {t(CHART_TRANSLATION.DESC_LABEL.key, CHART_TRANSLATION.DESC_LABEL.value)}
                  </span>
                  <FontstyleCombobox
                    t={t}
                    onSizeChange={setDescriptionFontsize}
                    fontColor={descriptionColor}
                    fontSize={descriptionFontSize}
                    onColorChange={setDescFontColor}
                  />
                </label>
              </ChartFontsizeSettings>
            </CollapsePanel>
          </ChartCollapse>
          <ChartCollapse>
            <CollapsePanel
              header={
                <div>
                  <Icon component={ChartTypesIcon} />{' '}
                  <span>
                    {t(
                      CHART_TRANSLATION.CHART_TYPE_LABEL.key,
                      CHART_TRANSLATION.CHART_TYPE_LABEL.value,
                    )}
                  </span>
                  <span>
                    <Icon component={ArrowDown} />
                  </span>
                </div>
              }
              key="1"
              style={panelStyle}
            >
              <ChartTypeSettings>
                {chartTypesList.map(item => (
                  <ChartTypeContainer
                    active={item.type === type}
                    onClick={() => setChartType(item)}
                  >
                    <img src={item.src} alt={item.alt} />
                    <div>{item.tag}</div>
                  </ChartTypeContainer>
                ))}
              </ChartTypeSettings>
            </CollapsePanel>
          </ChartCollapse>
          <ChartCollapse>
            <CollapsePanel
              header={
                <div>
                  <Icon component={chartStylesIcon} />{' '}
                  <span>
                    {t(
                      CHART_TRANSLATION.CHART_STYLE_LABEL.key,
                      CHART_TRANSLATION.CHART_STYLE_LABEL.value,
                    )}
                  </span>
                  <span>
                    <Icon component={ArrowDown} />
                  </span>
                </div>
              }
              key="1"
              style={panelStyle}
            >
              <ChartStylesSettings>
                <Radio.Group value={chartStyle} onChange={e => setChartStyle(e.target.value)}>
                  {chartStyles.map(item => (
                    <Radio.Button value={item.value}>{item.label}</Radio.Button>
                  ))}
                </Radio.Group>
              </ChartStylesSettings>
            </CollapsePanel>
          </ChartCollapse>
        </CollapseContainer>
      </TabPane>
    </Tabs>
  );
};
